import { useQuery } from "@tanstack/react-query";
import { Tag } from "lucide-react";
import { useId, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { helpdeskApi, useHelpdeskKey, useHelpdeskMutation } from "./api";

const LABEL_MAX = 40;

/** Etykieta adresu spoza aptek („Biuro”, „Dyrekcja”) w miejscu „niesparowane”. */
export function ContactLabelBadge({ label, className }: { label: string; className?: string }) {
	return (
		<span
			className={cn(badgeBase, "shrink-0 px-1.5 py-0", statusClasses.info, className)}
			title="Adres spoza aptek"
		>
			{label}
		</span>
	);
}

/**
 * Oznaczenie adresu jako spoza aptek (wolny tekst z podpowiedziami użytych etykiet),
 * zmiana i zdjęcie. Adres z etykietą znika z „Do sparowania”; toast pozwala cofnąć.
 */
export function ContactLabelButton({
	email,
	label,
	size = "sm",
}: {
	email: string;
	label: string | null;
	size?: "sm" | "xs";
}) {
	const [open, setOpen] = useState(false);
	const [draft, setDraft] = useState("");
	const listId = useId();
	const key = useHelpdeskKey();
	const labels = useQuery({
		queryKey: key("contact-labels"),
		queryFn: helpdeskApi.contactLabels,
		enabled: open,
		staleTime: 60_000,
	});
	// Toast w `onSuccess` hooka (przed odświeżeniem): karta w kolejce parowania zaraz znika.
	const save = useHelpdeskMutation(
		(next: string | null) => helpdeskApi.setContactLabel(email, next),
		{
			error: "Nie udało się zapisać etykiety",
			onSuccess: ({ label: next }) => {
				setOpen(false);
				toast.success(
					next ? `${email}: „${next}”` : `${email} wraca do parowania`,
					label === next
						? undefined
						: { action: { label: "Cofnij", onClick: () => save.mutate(label) } },
				);
			},
		},
	);
	const apply = (next: string | null) => save.mutate(next);
	const value = draft.trim();

	return (
		<>
			<Button
				size="sm"
				variant="outline"
				className={cn(size === "xs" && "h-6 px-2 text-xs")}
				onClick={() => {
					setDraft(label ?? "");
					setOpen(true);
				}}
			>
				<Tag className={cn("mr-1.5", size === "xs" ? "size-3" : "size-4")} aria-hidden />
				{label ? "Zmień etykietę" : "Spoza aptek"}
			</Button>
			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="max-w-md">
					<DialogHeader>
						<DialogTitle>Adres spoza aptek</DialogTitle>
						<DialogDescription>
							{email} — np. „Biuro” albo „Dyrekcja”. Adres zniknie z „Do sparowania”, a jego
							zgłoszenia nie dostaną podpowiedzi apteki ani diagnozy stanowisk.
						</DialogDescription>
					</DialogHeader>
					<DialogBody>
						<form
							id={`${listId}-form`}
							onSubmit={(e) => {
								e.preventDefault();
								if (value) apply(value);
							}}
						>
							<Input
								aria-label="Etykieta"
								autoFocus
								list={listId}
								maxLength={LABEL_MAX}
								placeholder="Biuro"
								value={draft}
								onChange={(e) => setDraft(e.target.value)}
							/>
							<datalist id={listId}>
								{labels.data?.map((l) => (
									<option key={l.label} value={l.label} />
								))}
							</datalist>
						</form>
					</DialogBody>
					<DialogFooter>
						{label ? (
							<Button
								variant="outline"
								className="mr-auto"
								disabled={save.isPending}
								onClick={() => apply(null)}
							>
								Usuń etykietę
							</Button>
						) : null}
						<Button variant="outline" onClick={() => setOpen(false)}>
							Anuluj
						</Button>
						<Button type="submit" form={`${listId}-form`} disabled={!value || save.isPending}>
							Zapisz
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
